import React, { useState, useEffect, ChangeEvent, useMemo } from 'react';
import { Table, Button, Input, Form, message, Space, Row, Col, Modal, Select } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import apiClient from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { fetchWithLoading } from '../utils/fetchHelper';
import { confirmDelete } from '../utils/confirmDelete';
import LocationForm from '../components/LocationForm';
import { accentInsensitiveIncludes } from '../utils/accentUtils';

const { TextArea } = Input;

interface Location {
  id: number;
  name: string;
  description?: string;
  company_id: number;
  parent_location_id?: number;
  company?: {
    name: string;
  };
  parent_location?: {
    name: string;
  };
}

const LocationPage = () => {
  const [form] = Form.useForm();
  const [locations, setLocations] = useState<Location[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const { user: currentUser, loading: authLoading } = useAuth();

  const companyMap = useMemo(() => Object.fromEntries(companies.map((c: any) => [c.id, c.name])), [companies]);
  const columns = useMemo(() => [
    { title: 'Tên vị trí', dataIndex: 'name', key: 'name' },
    { title: 'Mô tả', dataIndex: 'description', key: 'description', render: (text: string) => text || '-' },
    { title: 'Công ty', key: 'company', render: (_: any, record: Location) => companyMap[record.company_id] || '-' },
    { title: 'Vị trí cha', dataIndex: ['parent_location', 'name'], key: 'parent_location', render: (text: string, record: Location) => record.parent_location?.name || '-' },
    { title: 'Hành động', key: 'action', width: 110, render: (_: any, record: Location) => (<Space>{canEdit(record) && <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />}{canDelete(record) && <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteClick(record)} />}</Space>) }
  ], [companyMap, locations]);

  useEffect(() => {
    if (!currentUser) return;
    fetchLocations();
    fetchCompanies();
  }, [currentUser]);

  if (authLoading) return null; // hoặc spinner
  if (!currentUser || (currentUser.role !== 'super_admin' && currentUser.role !== 'company_admin' && currentUser.role !== 'manager')) {
    return <div>Bạn không có quyền truy cập trang này.</div>;
  }

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const defaultCompanyId = isSuperAdmin ? undefined : currentUser?.company_id;

  const fetchLocations = async () => {
    await fetchWithLoading(
      () => apiClient.getLocations(),
      setLoading,
      (data) => setLocations(data as Location[]),
      'Lỗi khi tải danh sách vị trí'
    );
  };

  const fetchCompanies = async () => {
    await fetchWithLoading(
      () => apiClient.getCompanies(),
      () => { },
      (data) => setCompanies(data as any[]),
      'Lỗi khi tải danh sách công ty'
    );
  };

  const handleSubmit = async (values: any) => {
    if (!isSuperAdmin) {
      values.company_id = defaultCompanyId;
    }
    try {
      if (editingLocation) {
        // Khi cập nhật, company_admin không được thay đổi company_id
        if (currentUser?.role === 'company_admin') {
          delete values.company_id;
        }
        await apiClient.updateLocation(editingLocation.id, values);
        message.success('Cập nhật vị trí thành công!');
      } else {
        await apiClient.createLocation(values);
        message.success('Tạo vị trí thành công!');
      }
      form.resetFields();
      setEditingLocation(null);
      setModalVisible(false);
      fetchLocations();
    } catch (err: any) {
      const msg = err?.response?.data?.message || (editingLocation ? 'Cập nhật thất bại!' : 'Tạo vị trí thất bại!');
      message.error(msg);
    }
  };

  const handleEdit = (record: Location) => {
    setEditingLocation(record);
    const formValues: any = {
      name: record.name,
      description: record.description,
      parent_location_id: record.parent_location_id,
    };
    // Chỉ super_admin mới được set company_id trong form
    if (currentUser?.role === 'super_admin') {
      formValues.company_id = record.company_id;
    }
    // KHÔNG set company_id nếu là company_admin
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteLocation(id);
      message.success('Đã xóa vị trí');
      fetchLocations();
    } catch {
      message.error('Xóa vị trí thất bại!');
    }
  };

  const handleDeleteClick = (record: Location) => {
    confirmDelete('Bạn có chắc muốn xóa vị trí này?', () => handleDelete(record.id));
  };

  const filteredLocations = locations.filter((l) =>
    accentInsensitiveIncludes(l.name, searchValue) ||
    (l.company?.name && accentInsensitiveIncludes(l.company.name, searchValue)) ||
    (l.description && accentInsensitiveIncludes(l.description, searchValue))
  );

  // Phân quyền thao tác
  const canEdit = (loc: Location) => {
    if (!currentUser) return false;
    if (currentUser.role === 'super_admin') return true;
    if ((currentUser.role === 'company_admin' || currentUser.role === 'manager') && loc.company_id === currentUser.company_id) return true;
    return false;
  };
  const canDelete = (loc: Location) => {
    if (!currentUser) return false;
    if (currentUser.role === 'super_admin') return true;
    if (currentUser.role === 'company_admin' && loc.company_id === currentUser.company_id) return true;
    return false;
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16, alignItems: 'center' }}>
        {currentUser.role === 'super_admin' ? (
          <>
            <Col xs={24} md={16}>
              <Input
                className="ams-input"
                placeholder="Tìm kiếm tên vị trí hoặc công ty"
                value={searchValue}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
                allowClear
                size="large"
                style={{ width: '100%', borderRadius: 8, height: 40 }}
              />
            </Col>
            <Col xs={24} md={4}>
              <Select
                className="ams-input"
                disabled
                size="large"
                style={{ width: '100%', borderRadius: 8, height: 40, display: 'none' }}
              />
            </Col>
            <Col xs={24} md={4}>
              <Button
                className="ams-btn"
                type="primary"
                size="large"
                icon={<EditOutlined />}
                style={{ borderRadius: 8, height: 40, width: '100%' }}
                onClick={() => {
                  setEditingLocation(null);
                  form.resetFields();
                  if (!isSuperAdmin && defaultCompanyId) {
                    form.setFieldsValue({ company_id: defaultCompanyId });
                  }
                  setModalVisible(true);
                }}
              >
                Thêm mới
              </Button>
            </Col>
          </>
        ) : ((currentUser.role === 'company_admin' || currentUser.role === 'manager') ? (
          <>
            <Col xs={24} md={20}>
              <Input
                className="ams-input"
                placeholder="Tìm kiếm tên vị trí hoặc công ty"
                value={searchValue}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
                allowClear
                size="large"
                style={{ width: '100%', borderRadius: 8, height: 40 }}
              />
            </Col>
            <Col xs={24} md={4}>
              <Button
                className="ams-btn"
                type="primary"
                size="large"
                icon={<EditOutlined />}
                style={{ borderRadius: 8, height: 40, width: '100%' }}
                onClick={() => {
                  setEditingLocation(null);
                  form.resetFields();
                  if (!isSuperAdmin && defaultCompanyId) {
                    form.setFieldsValue({ company_id: defaultCompanyId });
                  }
                  setModalVisible(true);
                }}
              >
                Thêm mới
              </Button>
            </Col>
          </>
        ) : (
          <Col xs={24} md={24}>
            <Input
              className="ams-input"
              placeholder="Tìm kiếm tên vị trí hoặc công ty"
              value={searchValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
              allowClear
              size="large"
              style={{ width: '100%', borderRadius: 8, height: 40 }}
            />
          </Col>
        ))}
      </Row>
      <Modal
        open={modalVisible}
        title={editingLocation ? 'Cập nhật vị trí' : 'Tạo mới vị trí'}
        onCancel={() => { setModalVisible(false); setEditingLocation(null); form.resetFields(); }}
        footer={null}
      >
        <LocationForm
          form={form}
          initialValues={editingLocation || {}}
          onFinish={handleSubmit}
          loading={loading}
          submitText={editingLocation ? 'Cập nhật' : 'Tạo mới'}
          companies={companies}
          locations={locations}
          isSuperAdmin={isSuperAdmin}
          currentUser={currentUser}
          editingLocation={editingLocation}
        />
        {editingLocation && (
          <Button
            className="ams-btn"
            style={{ width: '100%', borderRadius: 8, height: 40, marginTop: 8 }}
            size="large"
            onClick={() => {
              setEditingLocation(null);
              form.resetFields();
              setModalVisible(false);
            }}
          >
            Hủy
          </Button>
        )}
      </Modal>
      <Table
        dataSource={filteredLocations}
        rowKey="id"
        loading={loading}
        columns={columns}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default LocationPage; 